import { useCallback, useContext } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

// GetResponse marketing: all contacts live in ONE GetResponse list (configured
// via backend env); locations are expressed as tags on the contact — one tag
// per Standort.
function useGetResponseMarketing() {
  const authCtx = useContext(AuthContext);
  const { sendRequest } = useHttp();

  const authHeaders = useCallback(
    () => ({
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + authCtx.token,
    }),
    [authCtx.token]
  );

  // Entertainment list with current tag mapping + last sync result + main list:
  // { mainList, entertainments }.
  const getStatus = useCallback(async () => {
    return sendRequest({
      url: '/api/v1/getresponse-marketing/status',
      method: 'GET',
      headers: authHeaders(),
    });
  }, [sendRequest, authHeaders]);

  // All GetResponse campaigns/lists (e.g. as override options in the flow builder).
  const getCampaigns = useCallback(async () => {
    return sendRequest({
      url: '/api/v1/getresponse-marketing/campaigns',
      method: 'GET',
      headers: authHeaders(),
    });
  }, [sendRequest, authHeaders]);

  // All GetResponse tags (suggestions for tag pickers).
  const getTags = useCallback(async () => {
    return sendRequest({
      url: '/api/v1/getresponse-marketing/tags',
      method: 'GET',
      headers: authHeaders(),
    });
  }, [sendRequest, authHeaders]);

  // Persists the location tag for one entertainment (empty = derived default).
  const updateEntertainmentTag = useCallback(
    async (entertainmentId, tagName) => {
      return sendRequest({
        url: `/api/v1/getresponse-marketing/entertainment/${entertainmentId}/tag`,
        method: 'PATCH',
        headers: authHeaders(),
        body: { tagName },
      });
    },
    [sendRequest, authHeaders]
  );

  const postSync = useCallback(
    async entertainmentId => {
      return sendRequest({
        url: '/api/v1/getresponse-marketing/sync',
        method: 'POST',
        headers: authHeaders(),
        body: entertainmentId ? { entertainmentId } : {},
      });
    },
    [sendRequest, authHeaders]
  );

  return { getStatus, getCampaigns, getTags, updateEntertainmentTag, postSync };
}

export default useGetResponseMarketing;
